import React, { useEffect, useState } from 'react';
import { Plus, Search, Truck, Clock, Settings, AlertCircle, ChevronLeft, ChevronRight, Grid3X3, List } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

interface Maquinaria {
  id: string;
  codigo: string;
  nombre: string;
  tipo: string;
  clave_qr: string;
  patente: string | null;
  marca: string | null;
  modelo: string | null;
  anio: number | null;
  documentos?: Array<{
    id: string;
    tipo: string;
    storage_path: string | null;
    fecha_vencimiento: string | null;
  }>;
}

interface Horometro {
  lectura: number;
  maquinaria_id: string;
  alerta_mantenimiento: number;
}

interface DocumentAlert {
  maquinaria: string;
  documento: string;
  tipo: 'warning' | 'expired';
  dias: number;
}

// Componente de Paginación
const Pagination = ({ 
  currentPage, 
  totalPages, 
  totalItems,
  itemsPerPage,
  onPageChange 
}: {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
}) => {
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalItems);

  if (totalPages <= 1) return null;

  return (
    <div className="bg-white rounded-lg shadow-sm px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
      <div className="flex-1 flex justify-between sm:hidden">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md ${
            currentPage === 1
              ? 'border-gray-300 text-gray-400 bg-gray-100 cursor-not-allowed'
              : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
          }`}
        >
          Anterior
        </button>
        <span className="text-sm text-gray-700 flex items-center">
          Página {currentPage} de {totalPages}
        </span>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`ml-3 relative inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md ${
            currentPage === totalPages
              ? 'border-gray-300 text-gray-400 bg-gray-100 cursor-not-allowed'
              : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
          }`}
        >
          Siguiente
        </button>
      </div>
      
      <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-700">
            Mostrando <span className="font-medium">{startIndex}</span> a{' '}
            <span className="font-medium">{endIndex}</span> de{' '}
            <span className="font-medium">{totalItems}</span> maquinarias
          </p>
        </div>
        <div>
          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center px-2 py-2 rounded-l-md border text-sm font-medium ${
                currentPage === 1
                  ? 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'
              }`}
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
              if (
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 2 && page <= currentPage + 2)
              ) {
                return (
                  <button
                    key={page}
                    onClick={() => onPageChange(page)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      page === currentPage
                        ? 'z-10 bg-[#004A98] border-[#004A98] text-white'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                );
              }
              
              if (page === currentPage - 3 || page === currentPage + 3) {
                return (
                  <span key={page} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                    ...
                  </span>
                );
              }
              
              return null;
            })}
            
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`relative inline-flex items-center px-2 py-2 rounded-r-md border text-sm font-medium ${
                currentPage === totalPages
                  ? 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'
              }`}
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
};

// Componente de Alertas de Documentación con Paginación
const DocumentAlertsSection = ({ alerts, alertsPerPage = 5 }: { alerts: DocumentAlert[], alertsPerPage?: number }) => {
  const [currentPage, setCurrentPage] = useState(1);

  const sortedAlerts = [...alerts].sort((a, b) => {
    if (a.tipo === 'expired' && b.tipo === 'warning') return -1;
    if (a.tipo === 'warning' && b.tipo === 'expired') return 1;
    if (a.tipo === b.tipo) {
      return a.tipo === 'expired' 
        ? b.dias - a.dias
        : a.dias - b.dias;
    }
    return 0;
  });

  const totalPages = Math.ceil(sortedAlerts.length / alertsPerPage);
  const startIndex = (currentPage - 1) * alertsPerPage;
  const endIndex = startIndex + alertsPerPage;
  const currentAlerts = sortedAlerts.slice(startIndex, endIndex);

  const expiredCount = alerts.filter(a => a.tipo === 'expired').length;
  const warningCount = alerts.filter(a => a.tipo === 'warning').length;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      <div className="bg-gradient-to-r from-red-50 to-yellow-50 px-6 py-4 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-lg font-medium text-gray-900">Alertas de Documentación</h2>
            <p className="text-sm text-gray-500 mt-1">
              Documentos que requieren atención inmediata
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 bg-red-100 px-3 py-1 rounded-full">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <span className="text-sm font-medium text-red-700">{expiredCount} vencidos</span>
              </div>
              <div className="flex items-center gap-1 bg-yellow-100 px-3 py-1 rounded-full">
                <Clock className="h-4 w-4 text-yellow-600" />
                <span className="text-sm font-medium text-yellow-700">{warningCount} por vencer</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="space-y-3">
          {currentAlerts.map((alert, index) => (
            <div
              key={`${alert.maquinaria}-${alert.documento}-${index}`}
              className={`flex items-start p-4 rounded-lg border transition-all hover:shadow-md ${
                alert.tipo === 'expired' 
                  ? 'bg-red-50 border-red-200 hover:border-red-300' 
                  : 'bg-yellow-50 border-yellow-200 hover:border-yellow-300'
              }`}
            >
              <div className="flex-shrink-0 mt-0.5">
                {alert.tipo === 'expired' ? (
                  <AlertCircle className="h-5 w-5 text-red-500" />
                ) : (
                  <Clock className="h-5 w-5 text-yellow-500" />
                )}
              </div>
              
              <div className="ml-4 flex-1">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                  <div className="flex-1">
                    <h3 className={`text-sm font-semibold ${
                      alert.tipo === 'expired' ? 'text-red-900' : 'text-yellow-900'
                    }`}>
                      {alert.maquinaria}
                    </h3>
                    <p className={`text-sm mt-0.5 ${
                      alert.tipo === 'expired' ? 'text-red-700' : 'text-yellow-700'
                    }`}>
                      <span className="font-medium">{alert.documento}</span>
                      {' - '}
                      {alert.tipo === 'expired' 
                        ? `Vencido hace ${alert.dias} día${alert.dias !== 1 ? 's' : ''}`
                        : `Vence en ${alert.dias} día${alert.dias !== 1 ? 's' : ''}`
                      }
                    </p>
                  </div>
                  
                  <div className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                    alert.tipo === 'expired'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {alert.tipo === 'expired' ? 'Vencido' : 'Por vencer'}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="mt-6 border-t border-gray-200 pt-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Mostrando <span className="font-medium">{startIndex + 1}</span> a{' '}
                <span className="font-medium">{Math.min(endIndex, sortedAlerts.length)}</span> de{' '}
                <span className="font-medium">{sortedAlerts.length}</span> alertas
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    currentPage === 1
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                  }`}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Anterior
                </button>

                <div className="hidden sm:flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                            page === currentPage
                              ? 'bg-[#004A98] text-white'
                              : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    }
                    
                    if (page === currentPage - 2 || page === currentPage + 2) {
                      return (
                        <span key={page} className="px-2 text-gray-500">
                          ...
                        </span>
                      );
                    }
                    
                    return null;
                  })}
                </div>

                <div className="flex sm:hidden items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-md">
                  <span className="text-sm text-gray-700">
                    Página {currentPage} de {totalPages}
                  </span>
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    currentPage === totalPages
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                  }`}
                >
                  Siguiente
                  <ChevronRight className="h-4 w-4 ml-1" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const MaquinariasList = () => {
  const [maquinarias, setMaquinarias] = useState<(Maquinaria & { horometro?: Horometro })[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [documentAlerts, setDocumentAlerts] = useState<DocumentAlert[]>([]);
  
  // Estados de paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(9); // 9 para vista de grid (3x3)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  useEffect(() => {
    const fetchMaquinarias = async () => {
      try {
        const { data: maquinariasData, error: maquinariasError } = await supabase
          .from('maquinarias')
          .select(`
            *,
            documentos (
              id,
              tipo,
              storage_path,
              fecha_vencimiento
            )
          `);

        if (maquinariasError) {
          console.error('Error fetching maquinarias:', maquinariasError);
          throw maquinariasError;
        }

        if (!maquinariasData || maquinariasData.length === 0) {
          setMaquinarias([]);
          return;
        }

        const { data: horometrosData, error: horometrosError } = await supabase
          .from('horometros')
          .select()
          .in('maquinaria_id', maquinariasData.map(m => m.id))
          .order('fecha_lectura', { ascending: false });

        if (horometrosError) {
          console.error('Error fetching horometros:', horometrosError);
          throw horometrosError;
        }

        const latestHorometros = new Map();
        horometrosData?.forEach(horometro => {
          if (!latestHorometros.has(horometro.maquinaria_id)) {
            latestHorometros.set(horometro.maquinaria_id, horometro);
          }
        });

        const alerts: DocumentAlert[] = [];
        maquinariasData.forEach(maquinaria => {
          if (maquinaria.documentos) {
            maquinaria.documentos.forEach(doc => {
              if (doc.fecha_vencimiento && doc.storage_path) {
                const vencimiento = new Date(doc.fecha_vencimiento);
                const today = new Date();
                const diffDays = Math.ceil((vencimiento.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

                if (diffDays < 0) {
                  alerts.push({
                    maquinaria: maquinaria.nombre,
                    documento: doc.tipo,
                    tipo: 'expired',
                    dias: Math.abs(diffDays)
                  });
                } else if (diffDays <= 30) {
                  alerts.push({
                    maquinaria: maquinaria.nombre,
                    documento: doc.tipo,
                    tipo: 'warning',
                    dias: diffDays
                  });
                }
              }
            });
          }
        });

        setDocumentAlerts(alerts);

        const maquinariasConHorometro = maquinariasData.map(maquinaria => ({
          ...maquinaria,
          horometro: latestHorometros.get(maquinaria.id)
        }));

        setMaquinarias(maquinariasConHorometro);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err instanceof Error ? err.message : 'Error al cargar las maquinarias');
      } finally {
        setLoading(false);
      }
    };

    fetchMaquinarias();
  }, []);

  // Filtrar maquinarias
  const filteredMaquinarias = maquinarias.filter(maquinaria =>
    maquinaria.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    maquinaria.codigo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    maquinaria.tipo.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Cálculos de paginación
  const totalPages = Math.ceil(filteredMaquinarias.length / itemsPerPage);
  const paginatedMaquinarias = filteredMaquinarias.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset página cuando cambian los filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const getMaquinariaStatus = (maquinaria: Maquinaria & { horometro?: Horometro }) => {
    if (!maquinaria.horometro) return { label: 'Sin datos', color: 'gray' };
    
    const horasRestantes = maquinaria.horometro.alerta_mantenimiento - 
      (maquinaria.horometro.lectura % maquinaria.horometro.alerta_mantenimiento);
    
    if (horasRestantes <= 20) return { label: 'Mantenimiento próximo', color: 'yellow' };
    if (horasRestantes <= 0) return { label: 'Requiere mantenimiento', color: 'red' };
    return { label: 'Operativa', color: 'green' };
  };

  const handleViewModeChange = (mode: 'grid' | 'list') => {
    setViewMode(mode);
    setItemsPerPage(mode === 'grid' ? 9 : 10);
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004A98] mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando maquinarias...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow-sm">
        <div className="flex items-center space-x-4">
          <h1 className="text-2xl font-bold text-gray-900">Maquinarias</h1>
          <div className="h-6 w-px bg-gray-200"></div>
          <div className="text-sm text-gray-500">
            Total: {filteredMaquinarias.length}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Vista toggle */}
          <div className="flex border border-gray-300 rounded-md p-1">
            <button
              onClick={() => handleViewModeChange('grid')}
              className={`p-2 rounded ${
                viewMode === 'grid' 
                  ? 'bg-[#004A98] text-white' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              title="Vista de cuadrícula"
            >
              <Grid3X3 className="h-4 w-4" />
            </button>
            <button
              onClick={() => handleViewModeChange('list')}
              className={`p-2 rounded ${
                viewMode === 'list' 
                  ? 'bg-[#004A98] text-white' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              title="Vista de lista"
            >
              <List className="h-4 w-4" />
            </button>
          </div>
          
          <Link
            to="/maquinarias/nueva"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#004A98] hover:bg-[#003875] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#004A98]"
          >
            <Plus className="h-5 w-5 mr-2" />
            Nueva Maquinaria
          </Link>
        </div>
      </div>

      {/* Alertas de documentación */}
      {documentAlerts.length > 0 && (
        <DocumentAlertsSection alerts={documentAlerts} />
      )}

      {/* Buscador */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Buscar por código, nombre o tipo..."
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-[#004A98] focus:border-[#004A98] sm:text-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Lista de maquinarias con paginación */}
      {filteredMaquinarias.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <Truck className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No hay maquinarias</h3>
          <p className="mt-1 text-sm text-gray-500">
            Comienza agregando una nueva maquinaria a tu inventario.
          </p>
        </div>
      ) : (
        <>
          {/* Grid o Lista de maquinarias */}
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {paginatedMaquinarias.map((maquinaria) => {
                const status = getMaquinariaStatus(maquinaria);
                return (
                  <Link
                    key={maquinaria.id}
                    to={`/maquinarias/${maquinaria.id}`}
                    className="block bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden"
                  >
                    <div className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-1">
                            {maquinaria.nombre}
                          </h3>
                          <div className="space-y-1">
                            <p className="text-sm text-gray-500 flex items-center">
                              <Settings className="h-4 w-4 mr-1" />
                              {maquinaria.codigo}
                            </p>
                            <p className="text-sm text-gray-500">
                              Tipo: {maquinaria.tipo}
                            </p>
                          </div>
                        </div>
                        <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                          status.color === 'green' ? 'bg-green-100 text-green-800' :
                          status.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                          status.color === 'red' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {status.label}
                        </div>
                      </div>
                      
                      {maquinaria.horometro && (
                        <div className="mt-4 pt-4 border-t border-gray-100">
                          <div className="flex items-center justify-between text-sm">
                            <div className="flex items-center text-gray-500">
                              <Clock className="h-4 w-4 mr-1" />
                              Horómetro
                            </div>
                            <span className="font-medium text-gray-900">
                              {maquinaria.horometro.lectura} hrs
                            </span>
                          </div>
                          <div className="mt-2">
                            <div className="relative pt-1">
                              <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-100">
                                <div
                                  style={{
                                    width: `${((maquinaria.horometro.lectura % maquinaria.horometro.alerta_mantenimiento) / maquinaria.horometro.alerta_mantenimiento) * 100}%`
                                  }}
                                  className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-[#004A98]"
                                ></div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            // Vista de lista (tabla)
            <div className="bg-white shadow-sm rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Maquinaria
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Código
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tipo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Horómetro
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedMaquinarias.map((maquinaria) => {
                    const status = getMaquinariaStatus(maquinaria);
                    return (
                      <tr 
                        key={maquinaria.id} 
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => window.location.href = `/maquinarias/${maquinaria.id}`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {maquinaria.nombre}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{maquinaria.codigo}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{maquinaria.tipo}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            status.color === 'green' ? 'bg-green-100 text-green-800' :
                            status.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                            status.color === 'red' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {status.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {maquinaria.horometro ? (
                            <div className="flex items-center">
                              <span className="text-sm text-gray-900 mr-2">
                                {maquinaria.horometro.lectura} hrs
                              </span>
                              <div className="w-24">
                                <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200">
                                  <div
                                    style={{
                                      width: `${((maquinaria.horometro.lectura % maquinaria.horometro.alerta_mantenimiento) / maquinaria.horometro.alerta_mantenimiento) * 100}%`
                                    }}
                                    className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-[#004A98]"
                                  ></div>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-500">Sin datos</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Paginación */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filteredMaquinarias.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </>
      )}
    </div>
  );
};